import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Waves, Gauge, Droplets, ArrowRight } from 'lucide-react';

interface FlowRatePanelProps {
  value: string;
  onChange: (value: string) => void;
}

export default function FlowRatePanel({ value, onChange }: FlowRatePanelProps) {
  const numericValue = parseFloat(value) || 0;
  const [isDragging, setIsDragging] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Animated water flow canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resolve CSS variables → raw RGB strings (Canvas API can't use var())
    const style = getComputedStyle(document.documentElement);
    const canvasRGB = (style.getPropertyValue('--rb-canvas-rgb').trim() || '6,13,26');
    const cyanRGB   = (style.getPropertyValue('--rb-cyan-rgb').trim()   || '6,182,212');

    let animId: number;
    let offset = 0;

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // Background
      ctx.fillStyle = `rgba(${canvasRGB}, 0.5)`;
      ctx.fillRect(0, 0, w, h);

      // Flow intensity based on value — show gentle idle waves even at 0
      const intensity = Math.min(Math.max(numericValue / 500, 0.08), 1);
      const speed     = 0.5 + intensity * 3;
      const waveCount = 3 + Math.floor(intensity * 5);

      for (let i = 0; i < waveCount; i++) {
        ctx.beginPath();
        const alpha = 0.18 + (i / waveCount) * 0.55;
        ctx.strokeStyle = `rgba(${cyanRGB}, ${alpha})`;
        ctx.lineWidth = 1.5 + i * 0.5;

        for (let x = 0; x < w; x++) {
          const y = h / 2
            + Math.sin((x * 0.02) + offset * speed * 0.05 + i * 1.2) * (8  + intensity * 15)
            + Math.sin((x * 0.01) + offset * speed * 0.03 + i * 0.7) * (5  + intensity * 10);
          if (x === 0) ctx.moveTo(x, y);
          else         ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Flow direction arrows (visible from low intensity)
      if (intensity > 0.05) {
        const arrowCount = Math.floor(intensity * 6) + 1;
        for (let i = 0; i < arrowCount; i++) {
          const x = ((offset * speed * 2 + i * (w / arrowCount)) % (w + 40)) - 20;
          const y = h / 2 + Math.sin(i * 2.1) * 12;
          ctx.beginPath();
          ctx.fillStyle = `rgba(${cyanRGB}, ${0.28 + intensity * 0.35})`;
          ctx.moveTo(x + 10, y);
          ctx.lineTo(x,      y - 4);
          ctx.lineTo(x,      y + 4);
          ctx.closePath();
          ctx.fill();
        }
      }

      offset += 1;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, [numericValue]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className="rounded-xl border border-cyan-500/20 bg-rb-elevated overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-cyan-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
            <Waves className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-cyan-400">Flow Rate</h3>
            <p className="text-[10px] text-slate-500">Required only for initial assessment</p>
          </div>
        </div>
      </div>

      {/* Animated Canvas */}
      <div className="relative h-28 bg-rb-canvas">
        <canvas
          ref={canvasRef}
          width={400}
          height={112}
          className="w-full h-full"
        />
        {/* Overlay icon */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <motion.div
            animate={{ x: numericValue > 0 ? [0, 60, 0] : 0 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Droplets className="w-8 h-8 text-cyan-400/30" />
          </motion.div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-5 py-4 space-y-4">
        {/* Value display */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-cyan-400" />
            <span className="text-2xl font-bold text-slate-100 font-mono">
              {numericValue.toFixed(1)}
            </span>
            <span className="text-xs text-slate-500">m³/day</span>
          </div>
          <div className={`px-2 py-1 rounded text-[10px] font-medium ${
            numericValue > 0
              ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
              : 'bg-slate-800 text-slate-500 border border-slate-700'
          }`}>
            {numericValue > 0 ? 'Active' : 'Not Set'}
          </div>
        </div>

        {/* Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[10px] text-slate-500">
            <span>0</span>
            <span>250</span>
            <span>500</span>
            <span>750</span>
            <span>1000+</span>
          </div>
          <input
            type="range"
            min="0"
            max="1000"
            step="0.1"
            value={numericValue}
            onChange={handleSliderChange}
            className="w-full h-2 rounded-full appearance-none cursor-pointer
              bg-slate-800 border border-slate-700
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-5
              [&::-webkit-slider-thumb]:h-5
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-cyan-400
              [&::-webkit-slider-thumb]:border-2
              [&::-webkit-slider-thumb]:border-cyan-300
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:shadow-cyan-500/30
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:transition-transform
              [&::-webkit-slider-thumb]:hover:scale-110
              [&::-moz-range-thumb]:w-5
              [&::-moz-range-thumb]:h-5
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-cyan-400
              [&::-moz-range-thumb]:border-2
              [&::-moz-range-thumb]:border-cyan-300
              [&::-moz-range-thumb]:cursor-pointer
            "
          />
        </div>

        {/* Quick presets */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">Quick:</span>
          {[50, 100, 250, 500].map(preset => (
            <button
              key={preset}
              onClick={() => onChange(String(preset))}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                Math.abs(numericValue - preset) < 1
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700'
              }`}
            >
              {preset}
            </button>
          ))}
          <span className="text-[10px] text-slate-600">m³/day</span>
        </div>

        {/* Numeric input */}
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.1"
            value={value}
            onChange={handleSliderChange}
            placeholder="0.00"
            className="flex-1 px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/40"
          />
          <span className="text-xs text-slate-500">m³/day</span>
        </div>
      </div>
    </div>
  );
}
